import { MouseEventHandler } from 'react';
import type { PAGE_POSITIONS } from 'types';
import type { ArticleCard, ArticleCardImage } from 'types/capi/shared';

import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import Metadata, { MetadataSize, type MetadataTheme } from './Metadata';

type Variant =
  | '16/9-text-over'
  | '16/9-text-under'
  | '4/5-text-over'
  | '4/5-text-under';

type Props = {
  className?: string;
  item: ArticleCard;
  name: TrackedButtonClickName;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  options?: {
    expandedMetadata?: boolean;
    oneLineTitle?: boolean;
    theme?: MetadataTheme;
    titleSize?: MetadataSize;
  };
  pagePosition: PAGE_POSITIONS | `TEMP_${string}`;
  variant: Variant;
};

function getAspectClass(ratio: string) {
  return ratio === '4:5' ? 'aspect-[4/5]' : 'aspect-[16/9]';
}

function pickImage(
  images: ArticleCardImage[],
  ratio: ArticleCardImage['ratio']
): ArticleCardImage {
  return images.find((i) => i.ratio === ratio) ?? images[0]!;
}

export default function ArticleCard({
  className,
  item,
  name,
  onClick,
  options,
  pagePosition,
  variant
}: Props) {
  const { theme } = options ?? {};

  const isTextOver =
    variant === '16/9-text-over' || variant === '4/5-text-over';
  const ratio: ArticleCardImage['ratio'] =
    variant === '4/5-text-over' || variant === '4/5-text-under'
      ? '4:5'
      : '16:9';
  const image = pickImage(item.images, ratio);
  const resolvedTheme: MetadataTheme = theme ?? (isTextOver ? 'dark' : 'light');

  return (
    <Tracked.InternalLink
      to={item.link.path}
      variant="none"
      pagePosition={pagePosition}
      name={name}
      onClick={onClick}
      className={className}
    >
      {isTextOver ? (
        <TextOverImage
          image={image}
          item={item}
          options={{ ...options, theme: resolvedTheme }}
          ratio={ratio}
        />
      ) : (
        <TextUnderImage
          image={image}
          item={item}
          options={{ ...options, theme: resolvedTheme }}
          ratio={ratio}
        />
      )}
    </Tracked.InternalLink>
  );
}

type CardProps = {
  image: ArticleCardImage;
  item: ArticleCard;
  options: Props['options'];
  ratio: '4:5' | '16:9';
};

function TextUnderImage({ image, item, options, ratio }: CardProps) {
  const {
    expandedMetadata,
    oneLineTitle,
    theme = 'light',
    titleSize = 'sm'
  } = options ?? {};
  const aspectClass = getAspectClass(ratio);

  return (
    <div className="flex min-w-[285px] flex-col gap-2 text-start">
      <Image
        src={image.url}
        className={`w-full ${aspectClass} object-cover`}
        alt={image.alt}
        quality={100}
        loading="lazy"
        responsiveOptions={{
          md: { width: 900 },
          xs: { width: 672 }
        }}
      />
      <Metadata
        item={item}
        oneLineTitle={oneLineTitle}
        size={titleSize}
        theme={theme}
        expanded={expandedMetadata}
      />
    </div>
  );
}

function TextOverImage({ image, item, options, ratio }: CardProps) {
  const {
    expandedMetadata,
    oneLineTitle,
    theme = 'light',
    titleSize = 'md'
  } = options ?? {};
  const aspectClass = getAspectClass(ratio);

  return (
    <div
      className={`relative flex size-full min-h-[356px] flex-col items-center justify-end md:min-h-[450px] ${aspectClass}`}
    >
      <Image
        src={image.url}
        className="absolute z-10 block size-full object-cover"
        alt={image.alt}
        loading="lazy"
        responsiveOptions={{ xs: { height: 672, retainRatio: true } }}
        progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
        retainRatio
      />
      <div className="absolute inset-0 z-20 bg-gradient-to-b from-transparent to-black opacity-70" />
      <div className="z-30 flex size-full flex-col items-start justify-end gap-1 px-6 pb-6 text-white md:px-8 md:pb-8">
        <Metadata
          expanded={expandedMetadata}
          isTextOverImage
          item={item}
          oneLineTitle={oneLineTitle}
          size={titleSize}
          theme={theme}
        />
      </div>
    </div>
  );
}
